import { DiscordSDK, DiscordSDKMock, patchUrlMappings } from '@discord/embedded-app-sdk';

let discordSdk: DiscordSDK | DiscordSDKMock | null = null;
let isReady = false;

// Check if we're running inside Discord
export function isDiscordActivity(): boolean {
  if (typeof window === 'undefined') return false;

  // Discord Activities run on {app_id}.discordapp.com
  const hostname = window.location.hostname;
  if (hostname.endsWith('.discordapp.com')) {
    return true;
  }

  // Also check for query params (older/alternative method)
  const urlParams = new URLSearchParams(window.location.search);
  const frameId = urlParams.get('frame_id');
  const instanceId = urlParams.get('instance_id');
  const platform = urlParams.get('platform');

  return !!(frameId || instanceId || platform);
}

export async function initDiscordSDK(): Promise<DiscordSDK | DiscordSDKMock | null> {
  if (discordSdk) return discordSdk;

  if (!isDiscordActivity()) {
    console.log('Not running as Discord Activity');
    return null;
  }

  const clientId = import.meta.env.VITE_DISCORD_CLIENT_ID;

  if (!clientId) {
    console.error('Missing VITE_DISCORD_CLIENT_ID');
    return null;
  }

  try {
    discordSdk = new DiscordSDK(clientId);
    await discordSdk.ready();
    isReady = true;
    console.log('Discord SDK ready');

    // Set up URL mappings to proxy API requests through Discord
    // This must match the URL Mappings configured in Discord Developer Portal
    const apiUrl = import.meta.env.VITE_API_URL;
    if (apiUrl) {
      console.log('Setting up URL mappings for:', apiUrl);
      patchUrlMappings([
        { prefix: '/api', target: apiUrl },
      ], {
        patchFetch: true,
      });
    }

    return discordSdk;
  } catch (error) {
    console.error('Failed to initialize Discord SDK:', error);
    return null;
  }
}

export function getDiscordSDK(): DiscordSDK | DiscordSDKMock | null {
  return discordSdk;
}

export function isSDKReady(): boolean {
  return isReady;
}

export interface DiscordUser {
  id: string;
  username: string;
  discriminator: string;
  avatar: string | null;
  global_name: string | null;
}

export async function authenticateWithDiscord(): Promise<{
  user: DiscordUser;
  accessToken: string;
  apiToken: string;  // Our JWT for API calls
} | null> {
  const sdk = await initDiscordSDK();

  if (!sdk) {
    return null;
  }

  try {
    // Authorize with Discord
    const { code } = await sdk.commands.authorize({
      client_id: import.meta.env.VITE_DISCORD_CLIENT_ID,
      response_type: 'code',
      state: '',
      prompt: 'none',
      scope: ['identify', 'guilds'],
    });

    // Exchange code for token via our API
    // Use proxied URL when in Discord Activity (must match URL Mappings in Developer Portal)
    const tokenUrl = '/api/auth/discord/token';
    console.log('Exchanging code at:', tokenUrl);

    let response: Response;
    try {
      response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });
    } catch (fetchError) {
      console.error('Fetch error details:', {
        error: fetchError,
        url: tokenUrl,
        apiUrlEnv: import.meta.env.VITE_API_URL,
        location: window.location.href,
      });
      throw fetchError;
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Token exchange failed:', response.status, errorText);
      throw new Error(`Failed to exchange code for token: ${response.status}`);
    }

    const data = await response.json();
    console.log('Token exchange successful');

    // Authenticate with the SDK using the access token
    const auth = await sdk.commands.authenticate({
      access_token: data.access_token,
    });

    if (!auth) {
      throw new Error('Failed to authenticate with Discord SDK');
    }

    return {
      user: auth.user as DiscordUser,
      accessToken: data.access_token,
      apiToken: data.token,  // Our JWT for API calls
    };
  } catch (error) {
    console.error('Discord authentication failed:', error);
    return null;
  }
}

export async function getActivityParticipants(): Promise<string[]> {
  const sdk = getDiscordSDK();
  if (!sdk || !isReady) return [];

  try {
    // Get instance participants
    const participants = await sdk.commands.getInstanceConnectedParticipants();
    return participants.participants.map(p => p.id);
  } catch (error) {
    console.error('Failed to get participants:', error);
    return [];
  }
}

export async function getActivityChannel(): Promise<{ id: string; name: string } | null> {
  const sdk = getDiscordSDK();
  if (!sdk || !isReady || !sdk.channelId) return null;

  try {
    const channel = await sdk.commands.getChannel({ channel_id: sdk.channelId });
    if (channel) {
      return {
        id: channel.id,
        name: channel.name || 'Unknown Channel',
      };
    }
  } catch (error) {
    console.error('Failed to get channel:', error);
  }
  return null;
}

export async function getActivityGuild(): Promise<{ id: string; name: string } | null> {
  const sdk = getDiscordSDK();
  if (!sdk || !isReady) return null;

  try {
    if (sdk.guildId) {
      // We need to get guild info via API since SDK doesn't provide it directly
      return {
        id: sdk.guildId,
        name: 'Server', // Will be populated from API
      };
    }
  } catch (error) {
    console.error('Failed to get guild:', error);
  }
  return null;
}
